/**
 * Behavior Collector
 * Tracks user behavior: clicks, scrolls, form interactions, rage clicks
 */

import { BaseCollector } from './BaseCollector.js';
import type { EventBus } from '../core/EventBus.js';
import type { BreadcrumbManager } from '../utils/session.js';
import type { BehaviorEvent } from '../models/BehaviorEvent.js';
import { getElementSelector, isElementExcludedFromDeadClick, hasTextSelection } from '../utils/element.js';

// Constants for rage click detection
const MIN_CLICKS_PER_SECOND_TO_CONSIDER_RAGE = 3;
const ONE_SECOND = 1000;
const CLICK_PROXIMITY_THRESHOLD = 50; // pixels
const RAGE_CLICK_WINDOW = 5000; // Keep clicks for 5 seconds

interface ClickInfo {
  timestamp: number;
  targetSelector: string;
  targetTag: string;
  isExcludedFromDeadClick: boolean;
  x: number;
  y: number;
  hadScroll: boolean;
  hadSelection: boolean;
  hadInput: boolean;
}

export class BehaviorCollector extends BaseCollector {
  private breadcrumbManager: BreadcrumbManager;
  
  // Page engagement state
  private pageStartTime: number = Date.now();
  private maxScrollDepth: number = 0;
  private clickCount: number = 0;
  private keystrokeCount: number = 0;
  private formInteractions = new Map<string, { startTime: number; fields: Set<string>; completed: boolean }>();
  
  // Rage click detection
  private recentClicks: ClickInfo[] = [];
  private lastScrollTime: number = 0;
  private lastInputTime: number = 0;

  // Event listeners for cleanup
  private scrollListener: (() => void) | null = null;
  private clickListener: ((e: MouseEvent) => void) | null = null;
  private focusListener: ((e: FocusEvent) => void) | null = null;
  private keydownListener: ((e: KeyboardEvent) => void) | null = null;
  private inputListener: (() => void) | null = null;
  private submitListener: ((e: Event) => void) | null = null;
  private visibilityListener: (() => void) | null = null;

  // Timers
  private scrollTimer: number | undefined;
  private clickDebounceTimer: number | null = null;
  private pendingClicks: MouseEvent[] = [];

  constructor(
    eventBus: EventBus,
    sessionId: string,
    userId: string | null,
    breadcrumbManager: BreadcrumbManager,
    debug: boolean = false
  ) {
    super('BehaviorCollector', eventBus, sessionId, userId, debug);
    this.breadcrumbManager = breadcrumbManager;
  }

  initialize(): void {
    this.log('Initializing behavior tracking');

    // Track scroll activity
    this.scrollListener = () => {
      this.lastScrollTime = Date.now();
      
      // Scroll depth tracking with debounce
      clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        const scrollableHeight = document.body.scrollHeight - window.innerHeight;
        const scrollPercent = scrollableHeight > 0
          ? Math.round((window.scrollY / scrollableHeight) * 100)
          : 100;
        this.maxScrollDepth = Math.max(this.maxScrollDepth, Math.min(scrollPercent, 100));
      }, 100);
    };
    window.addEventListener('scroll', this.scrollListener);

    // Click tracking with rage click detection
    this.clickListener = (e: MouseEvent) => {
      this.clickCount++;
      this.pendingClicks.push(e);

      if (this.clickDebounceTimer !== null) {
        clearTimeout(this.clickDebounceTimer);
      }

      const delay = this.pendingClicks.length >= MIN_CLICKS_PER_SECOND_TO_CONSIDER_RAGE ? 10 : 50;
      this.clickDebounceTimer = window.setTimeout(() => this.processClicks(), delay);
    };
    document.addEventListener('click', this.clickListener);

    // Form interactions
    this.focusListener = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        const form = target.closest('form');
        const formId = form?.id || 'unnamed-form';

        if (!this.formInteractions.has(formId)) {
          this.formInteractions.set(formId, {
            startTime: Date.now(),
            fields: new Set(),
            completed: false
          });

          const baseMetadata = this.getBaseMetadata();
          const behaviorEvent: BehaviorEvent = {
            ...baseMetadata,
            event_category: 'behavior',
            event_type: 'form_start',
            time_on_page: null,
            scroll_depth: null,
            click_count: null,
            keystroke_count: null,
            rage_element: null,
            rage_element_class: null,
            rage_element_id: null,
            rage_click_count: null,
            rage_x: null,
            rage_y: null,
            rage_has_dead_clicks: null,
            form_id: formId,
            form_fields_completed: null,
            form_time_to_complete: null,
            form_time_spent: null,
            feature_name: null,
            conversion_event: null,
            conversion_value: null,
            context_json: null
          };

          this.emit('event:collected', behaviorEvent);
        }

        this.formInteractions.get(formId)!.fields.add(target.getAttribute('name') || target.getAttribute('id') || '');
        this.breadcrumbManager.add('input', `Input in ${target.tagName}`, {
          formId,
          fieldName: target.getAttribute('name')
        });
      }
    };
    document.addEventListener('focusin', this.focusListener);

    // Keystroke tracking
    this.keydownListener = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
        this.keystrokeCount++;
        this.lastInputTime = Date.now();
      }
    };
    document.addEventListener('keydown', this.keydownListener);

    // Input tracking
    this.inputListener = () => {
      this.lastInputTime = Date.now();
    };
    document.addEventListener('input', this.inputListener);

    // Form submissions
    this.submitListener = (e: Event) => {
      const form = e.target as HTMLFormElement;
      const formId = form.id || 'unnamed-form';
      const interaction = this.formInteractions.get(formId);

      if (interaction) {
        interaction.completed = true;
        
        const baseMetadata = this.getBaseMetadata();
        const behaviorEvent: BehaviorEvent = {
          ...baseMetadata,
          event_category: 'behavior',
          event_type: 'form_complete',
          time_on_page: null,
          scroll_depth: null,
          click_count: null,
          keystroke_count: null,
          rage_element: null,
          rage_element_class: null,
          rage_element_id: null,
          rage_click_count: null,
          rage_x: null,
          rage_y: null,
          rage_has_dead_clicks: null,
          form_id: formId,
          form_fields_completed: interaction.fields.size,
          form_time_to_complete: Date.now() - interaction.startTime,
          form_time_spent: null,
          feature_name: null,
          conversion_event: null,
          conversion_value: null,
          context_json: null
        };

        this.emit('event:collected', behaviorEvent);
      }
    };
    document.addEventListener('submit', this.submitListener);

    // Page engagement tracking
    this.visibilityListener = () => {
      if (document.visibilityState === 'hidden') {
        this.collectPageEngagement();
        this.collectFormAbandonments();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);

    this.isInitialized = true;
  }

  private processClicks(): void {
    if (this.pendingClicks.length === 0) return;

    const now = Date.now();

    this.pendingClicks.forEach((e) => {
      const target = e.target as Element;

      const hadRecentScroll = now - this.lastScrollTime < 100;
      const hadRecentInput = now - this.lastInputTime < 100;
      const hadSelection = hasTextSelection();

      this.recentClicks.push({
        timestamp: now,
        targetSelector: getElementSelector(target),
        targetTag: target.tagName,
        isExcludedFromDeadClick: isElementExcludedFromDeadClick(target),
        x: e.clientX,
        y: e.clientY,
        hadScroll: hadRecentScroll,
        hadSelection,
        hadInput: hadRecentInput
      });

      this.breadcrumbManager.add('click', `Clicked ${target.tagName}${target.id ? '#' + target.id : ''}`, {
        x: e.clientX,
        y: e.clientY
      });
    });

    // Clean up old clicks
    this.recentClicks = this.recentClicks.filter(click => now - click.timestamp < RAGE_CLICK_WINDOW);

    // Rage click detection
    if (this.recentClicks.length >= MIN_CLICKS_PER_SECOND_TO_CONSIDER_RAGE && this.isRageClick()) {
      const lastClick = this.pendingClicks[this.pendingClicks.length - 1];
      if (lastClick) {
        const target = lastClick.target as Element;
        const hasDeadClicks = this.recentClicks.some(click => this.isDeadClick(click));

        const baseMetadata = this.getBaseMetadata();
        const behaviorEvent: BehaviorEvent = {
          ...baseMetadata,
          event_category: 'behavior',
          event_type: 'rage_click',
          time_on_page: null,
          scroll_depth: null,
          click_count: null,
          keystroke_count: null,
          rage_element: target.tagName,
          rage_element_class: target.className.toString(),
          rage_element_id: target.id || null,
          rage_click_count: this.recentClicks.length,
          rage_x: lastClick.clientX,
          rage_y: lastClick.clientY,
          rage_has_dead_clicks: hasDeadClicks,
          form_id: null,
          form_fields_completed: null,
          form_time_to_complete: null,
          form_time_spent: null,
          feature_name: null,
          conversion_event: null,
          conversion_value: null,
          context_json: JSON.stringify({
            frustrationTypes: ['rage-click', ...(hasDeadClicks ? ['dead-click'] : [])]
          })
        };

        this.emit('event:collected', behaviorEvent);
        this.log(`Rage click detected on ${target.tagName}`);
        
        this.recentClicks = [];
      }
    }

    this.pendingClicks = [];
    this.clickDebounceTimer = null;
  }

  private isDeadClick(click: ClickInfo): boolean {
    if (click.hadScroll || click.hadInput || click.hadSelection) {
      return false;
    }
    return !click.isExcludedFromDeadClick;
  }

  private isRageClick(): boolean {
    if (this.recentClicks.some(click => click.hadSelection || click.hadScroll)) {
      return false;
    }

    for (let i = 0; i < this.recentClicks.length - (MIN_CLICKS_PER_SECOND_TO_CONSIDER_RAGE - 1); i++) {
      const endClick = this.recentClicks[i + MIN_CLICKS_PER_SECOND_TO_CONSIDER_RAGE - 1];
      const startClick = this.recentClicks[i];
      
      if (!endClick || !startClick) continue;
      
      const timeWindow = endClick.timestamp - startClick.timestamp;
      if (timeWindow <= ONE_SECOND) {
        const clicksInWindow = this.recentClicks.slice(i, i + MIN_CLICKS_PER_SECOND_TO_CONSIDER_RAGE);
        const firstClick = clicksInWindow[0];
        
        if (!firstClick) continue;
        
        const areProximate = clicksInWindow.every(
          (click) =>
            Math.abs(click.x - firstClick.x) <= CLICK_PROXIMITY_THRESHOLD &&
            Math.abs(click.y - firstClick.y) <= CLICK_PROXIMITY_THRESHOLD
        );
        
        if (areProximate) {
          return true;
        }
      }
    }
    
    return false;
  }

  private collectPageEngagement(): void {
    const baseMetadata = this.getBaseMetadata();
    const behaviorEvent: BehaviorEvent = {
      ...baseMetadata,
      event_category: 'behavior',
      event_type: 'page_engagement',
      time_on_page: Date.now() - this.pageStartTime,
      scroll_depth: this.maxScrollDepth,
      click_count: this.clickCount,
      keystroke_count: this.keystrokeCount,
      rage_element: null,
      rage_element_class: null,
      rage_element_id: null,
      rage_click_count: null,
      rage_x: null,
      rage_y: null,
      rage_has_dead_clicks: null,
      form_id: null,
      form_fields_completed: null,
      form_time_to_complete: null,
      form_time_spent: null,
      feature_name: null,
      conversion_event: null,
      conversion_value: null,
      context_json: null
    };

    this.emit('event:collected', behaviorEvent);
    this.log('Collected page engagement');
  }

  private collectFormAbandonments(): void {
    this.formInteractions.forEach((interaction, formId) => {
      if (!interaction.completed) {
        const baseMetadata = this.getBaseMetadata();
        const behaviorEvent: BehaviorEvent = {
          ...baseMetadata,
          event_category: 'behavior',
          event_type: 'form_abandon',
          time_on_page: null,
          scroll_depth: null,
          click_count: null,
          keystroke_count: null,
          rage_element: null,
          rage_element_class: null,
          rage_element_id: null,
          rage_click_count: null,
          rage_x: null,
          rage_y: null,
          rage_has_dead_clicks: null,
          form_id: formId,
          form_fields_completed: interaction.fields.size,
          form_time_to_complete: null,
          form_time_spent: Date.now() - interaction.startTime,
          feature_name: null,
          conversion_event: null,
          conversion_value: null,
          context_json: null
        };

        this.emit('event:collected', behaviorEvent);
      }
    });
  }

  destroy(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }

    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
      this.clickListener = null;
    }

    if (this.focusListener) {
      document.removeEventListener('focusin', this.focusListener);
      this.focusListener = null;
    }

    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }

    if (this.inputListener) {
      document.removeEventListener('input', this.inputListener);
      this.inputListener = null;
    }

    if (this.submitListener) {
      document.removeEventListener('submit', this.submitListener);
      this.submitListener = null;
    }

    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }

    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }

    if (this.clickDebounceTimer) {
      clearTimeout(this.clickDebounceTimer);
    }

    this.isInitialized = false;
    this.log('Destroyed');
  }
}

